import { useState } from "react";
import axios from "axios";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { COMPANY } from "@/data/content";
import { useToast } from "@/hooks/use-toast";
import { Instagram, Linkedin, ArrowRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INQUIRY_TYPES = [
  "Creator representation",
  "Brand partnership",
  "Business development",
  "Lennon Global (companies)",
  "Press",
  "General",
];

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "", email: "", company: "", inquiry_type: "Creator representation", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast({ title: "Sent.", description: "We'll be in touch shortly." });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : Array.isArray(detail) ? detail.map((d) => d.msg).join(", ")
        : "Something went wrong. Please try again.";
      toast({ title: "Submission failed", description: msg, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        kicker="Contact"
        title='Start the <span class="text-warm-600">conversation</span>.'
        intro="Whether you're a creator looking for long-term representation, a brand or publisher building a meaningful partnership, or a company exploring Lennon Global — tell us what you're working on. We typically reply within two business days."
      />

      <section className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-14 pb-32">
        <div className="grid md:grid-cols-12 gap-16">
          <Reveal className="md:col-span-5 order-2 md:order-1">
            <div className="space-y-10">
              <div>
                <div className="eyebrow mb-3">Direct</div>
                <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-link" className="display text-ink text-3xl hover:text-warm-600 transition-colors">
                  {COMPANY.email}
                </a>
              </div>
              <div>
                <div className="eyebrow mb-3">Offices</div>
                <ul className="space-y-2">
                  {COMPANY.studios.map((s) => (
                    <li key={s.city}>
                      <div className="display text-ink text-xl md:text-2xl">{s.city}<span className="text-warm-600 text-base"> · {s.region.split(",")[0]}</span></div>
                      <div className="eyebrow text-warm-600 mt-0.5">{s.note}</div>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="eyebrow mb-3">Follow</div>
                <div className="flex items-center gap-6">
                  <a href={COMPANY.socials.instagram} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="inline-flex items-center gap-3 text-ink hover:text-warm-600">
                    <Instagram size={20} /><span className="text-sm">Instagram</span>
                  </a>
                  <a href={COMPANY.socials.linkedin} target="_blank" rel="noreferrer" data-testid="contact-linkedin" className="inline-flex items-center gap-3 text-ink hover:text-warm-600">
                    <Linkedin size={20} /><span className="text-sm">LinkedIn</span>
                  </a>
                </div>
              </div>
            </div>
          </Reveal>

          <div className="md:col-span-7 order-1 md:order-2">
            {submitted ? (
              <Reveal>
                <div data-testid="contact-success" className="border border-line bg-mute p-10 md:p-14">
                  <CheckCircle2 size={32} className="text-ink" />
                  <h3 className="mt-6 display text-ink text-4xl md:text-5xl">Thank you.</h3>
                  <p className="mt-4 text-ink-800 leading-relaxed max-w-md text-lg">
                    Your note is in our inbox. A member of the team will reply within
                    two business days.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setSubmitted(false); setForm({ name: "", email: "", company: "", inquiry_type: "Creator representation", message: "" }); }}
                    data-testid="contact-send-another"
                    className="mt-8 inline-flex items-center gap-3 eyebrow text-ink hover:text-warm-600"
                  >
                    Send another <ArrowRight size={16} />
                  </button>
                </div>
              </Reveal>
            ) : (
              <Reveal>
                <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-10">
                  <div className="grid md:grid-cols-2 gap-10">
                    <div>
                      <label className="eyebrow block mb-3">Name</label>
                      <input required type="text" value={form.name} onChange={update("name")} data-testid="contact-input-name"
                        className="w-full bg-transparent border-b border-ink/30 focus:border-ink outline-none py-3 text-ink placeholder:text-warm-400 transition-colors"
                        placeholder="Your full name" />
                    </div>
                    <div>
                      <label className="eyebrow block mb-3">Email</label>
                      <input required type="email" value={form.email} onChange={update("email")} data-testid="contact-input-email"
                        className="w-full bg-transparent border-b border-ink/30 focus:border-ink outline-none py-3 text-ink placeholder:text-warm-400"
                        placeholder="you@company.com" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-10">
                    <div>
                      <label className="eyebrow block mb-3">Company</label>
                      <input type="text" value={form.company} onChange={update("company")} data-testid="contact-input-company"
                        className="w-full bg-transparent border-b border-ink/30 focus:border-ink outline-none py-3 text-ink placeholder:text-warm-400"
                        placeholder="Optional" />
                    </div>
                    <div>
                      <label className="eyebrow block mb-3">Inquiry</label>
                      <select value={form.inquiry_type} onChange={update("inquiry_type")} data-testid="contact-input-inquiry"
                        className="w-full bg-transparent border-b border-ink/30 focus:border-ink outline-none py-3 text-ink">
                        {INQUIRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="eyebrow block mb-3">Message</label>
                    <textarea required rows={6} value={form.message} onChange={update("message")} data-testid="contact-input-message"
                      className="w-full bg-transparent border-b border-ink/30 focus:border-ink outline-none py-3 text-ink resize-none placeholder:text-warm-400"
                      placeholder="Tell us what you're working on — the briefer the better." />
                  </div>
                  <div className="flex items-center justify-between gap-6">
                    <p className="text-sm text-warm-600 max-w-sm">By submitting, you agree to be contacted by Lennon about your inquiry.</p>
                    <button type="submit" disabled={submitting} data-testid="contact-submit"
                      className="inline-flex items-center gap-3 bg-ink text-paper px-7 py-4 text-sm font-medium hover:bg-ink-800 transition-colors disabled:opacity-50">
                      {submitting ? "Sending..." : "Send"} <ArrowRight size={16} />
                    </button>
                  </div>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
